import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { CreditCard, List, Package } from 'lucide-react';

import {
  Content,
  ListContent,
  FormScrollArea,
  FormMainContainer,
} from './style';

import { FinancialEntriesOutletContext, FinancialEntryListFilters } from './types';
import {
  FINANCIAL_ENTRY_SORT_BY,
  FINANCIAL_VIEW_BY,
  FinancialViewByValue,
  getDefaultSortForView,
  getSortOptionsForView,
} from './constants';
import { financialEntryApi } from './api';
import { registerPaymentMethodCatalog } from '../../../utils/paymentMethodLabels';
import { getFinancialFilterHeaders } from './financialFilterConfig';
import { ViewModeSelector } from '../Pipelines/PipelineControls';

import { TypeMainOutlet } from '..';

import {
  FinancialFormActionsProvider,
  useFinancialFormActions,
} from './FinancialFormActionsContext';

import { CancelButton, SaveButton } from './FinancialEntryForm/style';
import { PageHeader } from '../../../components/StandardPage';
import { Filter } from '../../../components/SubHeaderFilter/types';
import {
  financialEntryRoutes,
  isFinancialEntryEditPath,
  isFinancialEntryFormPath,
} from './routes';

const FinancialEntriesLayoutInner: React.FC = () => {
  const location = useLocation();
  const parentContext = useOutletContext<TypeMainOutlet>();
  const navigate = useNavigate();
  const { triggerSave, saving } = useFinancialFormActions();

  const search = parentContext?.search ?? '';

  const [filters, setFilters] = useState<FinancialEntryListFilters>({
    sortBy: FINANCIAL_ENTRY_SORT_BY.CREATED_AT,
    viewBy: FINANCIAL_VIEW_BY.ENTRY,
    appliedFilters: [],
  });

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    financialEntryApi
      .getPaymentMethods()
      .then(registerPaymentMethodCatalog)
      .catch(() => undefined);
  }, []);

  const isFormPage = isFinancialEntryFormPath(location.pathname);
  const isEditPage = isFinancialEntryEditPath(location.pathname);

  useEffect(() => {
    if (!isEditPage) return;
    const params = new URLSearchParams(location.search);
    if (params.get('readOnly') === 'true' || params.get('mode') === 'view') {
      navigate(financialEntryRoutes.list, { replace: true });
    }
  }, [isEditPage, location.search, navigate]);

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const outletContext: FinancialEntriesOutletContext = {
    search,
    filters,
    setFilters,
    refreshKey,
    triggerRefresh,
  };

  const handleFiltersChange = (patch: Partial<FinancialEntryListFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const appliedFilters = filters.appliedFilters ?? [];
  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const filterHeaders = getFinancialFilterHeaders(viewBy);

  const handleApplyFilter = (filter: Filter) => {
    handleFiltersChange({ appliedFilters: [...appliedFilters, filter] });
  };

  const handleRemoveFilter = (index: number) => {
    handleFiltersChange({
      appliedFilters: appliedFilters.filter((_, i) => i !== index),
    });
  };

  const handleClearAllFilters = () => {
    handleFiltersChange({ appliedFilters: [] });
  };

  const listViewSelector = (
    <ViewModeSelector
      value={viewBy}
      options={[
        { value: FINANCIAL_VIEW_BY.ENTRY, label: 'Lançamentos', icon: <List size={16} /> },
        { value: FINANCIAL_VIEW_BY.PAYMENT, label: 'Pagamentos', icon: <CreditCard size={16} /> },
        { value: FINANCIAL_VIEW_BY.ITEM, label: 'Itens', icon: <Package size={16} /> },
      ]}
      onChange={(nextViewBy: FinancialViewByValue) => {
        const nextSortOptions = getSortOptionsForView(nextViewBy);
        const currentSort = filters.sortBy ?? getDefaultSortForView(viewBy);
        const sortBy = nextSortOptions.some((option) => option.value === currentSort)
          ? currentSort
          : getDefaultSortForView(nextViewBy);

        handleFiltersChange({
          viewBy: nextViewBy,
          sortBy,
          appliedFilters: [],
        });
      }}
    />
  );

  return (
    <>
      {isFormPage ? (
        <PageHeader
          title={isEditPage ? 'Editar lançamento' : 'Novo lançamento'}
          extraActions={
            <>
              <CancelButton type="button" onClick={() => navigate(financialEntryRoutes.list)}>
                Cancelar
              </CancelButton>
              <SaveButton type="button" disabled={saving} onClick={triggerSave}>
                {saving ? 'Salvando...' : isEditPage ? 'Salvar alterações' : 'Criar lançamento'}
              </SaveButton>
            </>
          }
        />
      ) : (
        <PageHeader
          title="Financeiro"
          onReload={triggerRefresh}
          rightExtra={listViewSelector}
          filterHeaders={filterHeaders}
          appliedFilters={appliedFilters}
          onApplyFilter={handleApplyFilter}
          onRemoveFilter={handleRemoveFilter}
          onClearAllFilters={handleClearAllFilters}
          createButton={{
            label: 'Novo lançamento',
            onClick: () => navigate(financialEntryRoutes.new),
          }}
        />
      )}

      {isFormPage ? (
        <Content>
          <FormScrollArea>
            <FormMainContainer>
              <Outlet context={outletContext} />
            </FormMainContainer>
          </FormScrollArea>
        </Content>
      ) : (
        <ListContent>
          <Outlet context={outletContext} />
        </ListContent>
      )}
    </>
  );
};

const FinancialEntriesLayout: React.FC = () => (
  <FinancialFormActionsProvider>
    <FinancialEntriesLayoutInner />
  </FinancialFormActionsProvider>
);

export default FinancialEntriesLayout;
